import * as d3 from "d3";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { GicsTreeNode } from "../types.ts";

interface Props {
  sectors: GicsTreeNode[];
  selectedNodeId?: string | null;
  onNodeClick?: (nodeId: string) => void;
}

export interface IndustriesTreeRef {
  expandToNode: (ancestorIds: string[], targetId: string) => Promise<void>;
}

interface D3TreeNode extends d3.HierarchyPointNode<GicsTreeNode> {
  x0?: number;
  y0?: number;
  _children?: D3TreeNode[];
}

const LEVEL_COLORS: Record<number, string> = {
  0: "oklch(0.5 0.1 280)", // Root
  1: "oklch(0.6 0.18 280)", // Sector - purple
  2: "oklch(0.65 0.15 200)", // Industry Group - blue
  3: "oklch(0.7 0.15 150)", // Industry - teal
  4: "oklch(0.75 0.12 100)", // Sub-Industry - green
};

const LEVEL_NAMES: Record<number, string> = {
  1: "Sector",
  2: "Industry Group",
  3: "Industry",
  4: "Sub-Industry",
};

const NODE_WIDTH = 180;
const NODE_HEIGHT = 40;
const HORIZONTAL_SPACING = 20;
const VERTICAL_SPACING = 80;

const IndustriesTree = forwardRef<IndustriesTreeRef, Props>(
  function IndustriesTree({ sectors, selectedNodeId, onNodeClick }, ref) {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState<{
      width: number;
      height: number;
    } | null>(null);
    const initializedRef = useRef(false);

    // Refs to store D3 functions for external access
    const d3Refs = useRef<{
      root: D3TreeNode | null;
      update: ((source: D3TreeNode) => void) | null;
      zoom: d3.ZoomBehavior<SVGSVGElement, unknown> | null;
      svg: d3.Selection<SVGSVGElement, unknown, null, undefined> | null;
      findNode: ((id: string) => D3TreeNode | undefined) | null;
      loadAndExpandNode: ((node: D3TreeNode) => Promise<void>) | null;
    }>({
      root: null,
      update: null,
      zoom: null,
      svg: null,
      findNode: null,
      loadAndExpandNode: null,
    });

    useImperativeHandle(ref, () => ({
      expandToNode: async (ancestorIds: string[], targetId: string) => {
        const { root, update, findNode, loadAndExpandNode, zoom, svg } =
          d3Refs.current;
        if (
          !root ||
          !update ||
          !findNode ||
          !loadAndExpandNode ||
          !zoom ||
          !svg
        )
          return;

        // Expand each ancestor in order
        for (const ancestorId of ancestorIds) {
          const node = findNode(ancestorId);
          if (node && !node.children && node._children) {
            await loadAndExpandNode(node);
          }
        }

        // Find and highlight target node
        const targetNode = findNode(targetId);
        if (targetNode && dimensions) {
          const { width, height } = dimensions;
          const scale = 1.5;
          const x = width / 2 - (targetNode.x ?? 0) * scale;
          const y = height / 2 - (targetNode.y ?? 0) * scale;

          svg
            .transition()
            .duration(500)
            .call(zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale));

          // Highlight the node temporarily
          const nodeElement = svg.select(`g.node[data-id="${targetId}"]`);
          if (!nodeElement.empty()) {
            nodeElement.classed("highlighted", true);
            setTimeout(() => {
              nodeElement.classed("highlighted", false);
            }, 2000);
          }
        }
      },
    }));

    useEffect(() => {
      if (!containerRef.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
      if (
        !svgRef.current ||
        !sectors.length ||
        !dimensions ||
        initializedRef.current
      )
        return;
      initializedRef.current = true;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const { width } = dimensions;
      const margin = { top: 60, right: 40, bottom: 40, left: 40 };

      const virtualRoot: GicsTreeNode = {
        id: "root",
        code: "",
        name: "GICS",
        level: 0 as 1 | 2 | 3 | 4,
        children: sectors,
      };

      const root = d3.hierarchy(virtualRoot) as D3TreeNode;
      root.x0 = 0;
      root.y0 = 0;

      const zoom = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 3])
        .on("zoom", (event) => {
          g.attr("transform", event.transform);
        });

      svg.call(zoom);

      // Store refs for external access
      d3Refs.current.root = root;
      d3Refs.current.zoom = zoom;
      d3Refs.current.svg = svg;

      svg.append("defs").html(`
      <style>
        @keyframes pulse-highlight {
          0%, 100% {
            stroke-width: 2;
            filter: drop-shadow(0 0 0 transparent);
          }
          50% {
            stroke-width: 4;
            filter: drop-shadow(0 0 8px oklch(0.6 0.18 280));
          }
        }
        .highlighted rect:first-of-type {
          animation: pulse-highlight 0.6s ease-in-out 3;
          stroke: oklch(0.6 0.18 280) !important;
        }
        .selected rect:first-of-type {
          stroke-width: 3;
          stroke: oklch(0.6 0.18 280);
          filter: drop-shadow(0 0 4px oklch(0.6 0.18 280 / 0.4));
        }
        .node.leaf-node:hover rect:first-of-type {
          stroke-width: 2.5;
          cursor: pointer;
        }
      </style>
    `);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const treeLayout = d3
        .tree<GicsTreeNode>()
        .nodeSize([
          NODE_WIDTH + HORIZONTAL_SPACING,
          NODE_HEIGHT + VERTICAL_SPACING,
        ]);

      // Helper to find a node by ID
      const findNode = (id: string): D3TreeNode | undefined => {
        const search = (node: D3TreeNode): D3TreeNode | undefined => {
          if (node.data.id === id) return node;
          const children = node.children || node._children || [];
          for (const child of children) {
            const found = search(child as D3TreeNode);
            if (found) return found;
          }
          return undefined;
        };
        return search(root);
      };

      d3Refs.current.findNode = findNode;

      // Helper to expand a node (children are pre-loaded)
      const loadAndExpandNode = async (node: D3TreeNode): Promise<void> => {
        if (node.children) return; // Already expanded

        if (node._children) {
          // Collapsed, just expand
          node.children = node._children;
          node._children = undefined;
          update(node);
        }
      };

      d3Refs.current.loadAndExpandNode = loadAndExpandNode;

      const update = (source: D3TreeNode) => {
        d3Refs.current.update = update;
        const duration = 300;

        const treeData = treeLayout(root);
        const nodes = treeData.descendants() as D3TreeNode[];
        const links = treeData.links();

        nodes.forEach((d) => {
          d.y = d.depth * (NODE_HEIGHT + VERTICAL_SPACING);
        });

        // ========== NODES ==========
        const node = g
          .selectAll<SVGGElement, D3TreeNode>("g.node")
          .data(nodes, (d) => d.data.id);

        const nodeEnter = node
          .enter()
          .append("g")
          .attr("class", (d) => {
            const classes = ["node"];
            if (d.data.level === 4) classes.push("leaf-node");
            return classes.join(" ");
          })
          .attr("data-id", (d) => d.data.id)
          .attr(
            "transform",
            () => `translate(${source.x0 ?? 0},${source.y0 ?? 0})`,
          )
          .style("cursor", "pointer")
          .on("click", (event, d) => {
            if (d.data.level === 4 && onNodeClick) {
              event.stopPropagation();
              onNodeClick(d.data.id);
            }
          });

        // Node background
        nodeEnter
          .append("rect")
          .attr("width", NODE_WIDTH)
          .attr("height", NODE_HEIGHT)
          .attr("x", -NODE_WIDTH / 2)
          .attr("y", -NODE_HEIGHT / 2)
          .attr("rx", 6)
          .attr("ry", 6)
          .attr("fill", "oklch(0.98 0.01 280)")
          .attr("stroke", (d) => LEVEL_COLORS[d.data.level] || "#666")
          .attr("stroke-width", 2);

        // Level color bar
        nodeEnter
          .append("rect")
          .attr("width", 4)
          .attr("height", NODE_HEIGHT - 8)
          .attr("x", -NODE_WIDTH / 2 + 4)
          .attr("y", -NODE_HEIGHT / 2 + 4)
          .attr("rx", 2)
          .attr("fill", (d) => LEVEL_COLORS[d.data.level] || "#666");

        // Node name
        nodeEnter
          .append("text")
          .attr("dy", "-0.1em")
          .attr("x", -NODE_WIDTH / 2 + 14)
          .attr("text-anchor", "start")
          .attr("font-size", "10px")
          .attr("font-weight", "500")
          .attr("fill", "#333")
          .text((d) => {
            const name = d.data.name;
            return name.length > 22 ? `${name.substring(0, 20)}...` : name;
          })
          .append("title")
          .text((d) => d.data.name);

        // Code and level label
        nodeEnter
          .filter((d) => d.data.level > 0)
          .append("text")
          .attr("dy", "1em")
          .attr("x", -NODE_WIDTH / 2 + 14)
          .attr("text-anchor", "start")
          .attr("font-size", "9px")
          .attr("fill", "#888")
          .text((d) => `${d.data.code} · ${LEVEL_NAMES[d.data.level] || ""}`);

        // Expand/collapse button
        const expandButton = nodeEnter
          .filter((d) => !!(d.children || d._children))
          .append("g")
          .attr("class", "expand-button")
          .style("cursor", "pointer")
          .on("click", (event, d) => {
            event.stopPropagation();
            if (d.children) {
              d._children = d.children as D3TreeNode[];
              d.children = undefined;
              update(d);
            } else if (d._children) {
              d.children = d._children;
              d._children = undefined;
              update(d);
            }
          });

        expandButton
          .append("rect")
          .attr("x", NODE_WIDTH / 2 - 20)
          .attr("y", -10)
          .attr("width", 20)
          .attr("height", 20)
          .attr("fill", "transparent");

        expandButton
          .append("text")
          .attr("class", "expand-indicator")
          .attr("x", NODE_WIDTH / 2 - 10)
          .attr("y", 4)
          .attr("text-anchor", "middle")
          .attr("font-size", "14px")
          .attr("font-weight", "bold")
          .attr("fill", "#666")
          .text((d) => (d.children ? "−" : "+"));

        const nodeUpdate = nodeEnter.merge(node);

        nodeUpdate
          .transition()
          .duration(duration)
          .attr("transform", (d) => `translate(${d.x},${d.y})`);

        nodeUpdate
          .select(".expand-indicator")
          .text((d) => (d.children ? "−" : "+"));

        node
          .exit()
          .transition()
          .duration(duration)
          .attr("transform", () => `translate(${source.x},${source.y})`)
          .remove();

        // ========== LINKS ==========
        const link = g
          .selectAll<SVGPathElement, d3.HierarchyPointLink<GicsTreeNode>>(
            "path.link",
          )
          .data(links, (d) => d.target.data.id);

        const diagonal = (d: {
          source: { x: number; y: number };
          target: { x: number; y: number };
        }) => {
          return `M${d.source.x},${d.source.y + NODE_HEIGHT / 2}
                C${d.source.x},${(d.source.y + d.target.y) / 2}
                 ${d.target.x},${(d.source.y + d.target.y) / 2}
                 ${d.target.x},${d.target.y - NODE_HEIGHT / 2}`;
        };

        const linkEnter = link
          .enter()
          .insert("path", "g")
          .attr("class", "link")
          .attr("fill", "none")
          .attr("stroke", "#ccc")
          .attr("stroke-width", 1.5)
          .attr("d", () => {
            const o = { x: source.x0 ?? 0, y: source.y0 ?? 0 };
            return diagonal({ source: o, target: o });
          });

        linkEnter
          .merge(link)
          .transition()
          .duration(duration)
          .attr("d", (d) =>
            diagonal({
              source: { x: d.source.x, y: d.source.y },
              target: { x: d.target.x, y: d.target.y },
            }),
          );

        link
          .exit()
          .transition()
          .duration(duration)
          .attr("d", () => {
            const o = { x: source.x, y: source.y };
            return diagonal({ source: o, target: o });
          })
          .remove();

        nodes.forEach((d) => {
          d.x0 = d.x;
          d.y0 = d.y;
        });
      };

      update(root);

      const initialTransform = d3.zoomIdentity.translate(width / 2, margin.top);
      svg.call(zoom.transform, initialTransform);
    }, [sectors, dimensions, onNodeClick]);

    useEffect(() => {
      if (!svgRef.current) return;
      const svg = d3.select(svgRef.current);
      svg.selectAll("g.node").classed("selected", false);
      if (selectedNodeId) {
        svg
          .select(`g.node[data-id="${selectedNodeId}"]`)
          .classed("selected", true);
      }
    }, [selectedNodeId]);

    return (
      <div
        ref={containerRef}
        className="w-full h-full bg-card rounded-b-lg border-x border-b border-border overflow-hidden"
      >
        <svg
          ref={svgRef}
          width={dimensions?.width ?? "100%"}
          height={dimensions?.height ?? "100%"}
          className="block"
        />
      </div>
    );
  },
);

export default IndustriesTree;
